import { getBaseUrl } from 'api/domains';
import endpoints from 'api/endpoints';
import Axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import useCookies from 'hooks/useCookies';
import useIsServer from 'hooks/useIsServer';
import useLocale from 'hooks/useLocale';
import get from 'lodash/get';
import { HttpMethods } from './types';

const UNAUTHORIZED = 401;

const clientInfo = {
  platform: 5,
};

// init memory and diskCache cache
const defaultTtlS = 5 * 60; // 5mn
const MaxPayloadSizeForMemoryCache = 1e6;
// const MaxCachedPayloadsInMemory = 1000;

const memoryCache = undefined as any;
let diskCache = undefined as any;

const requestsWaitingResponse = {} as { [key: string]: Promise<any> };

if (useIsServer()) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires,global-require
  const fs = require('fs');

  // eslint-disable-next-line @typescript-eslint/no-var-requires,global-require
  const cacheManager = require('cache-manager');

  // eslint-disable-next-line @typescript-eslint/no-var-requires,global-require
  const fsStore = require('cache-manager-fs-hash');

  const cacheDir = process.env.CACHE_PATH || 'apiCache';

  fs.mkdirSync(cacheDir, { recursive: true });

  diskCache = cacheManager.caching({
    store: fsStore,
    options: {
      path: cacheDir,
      ttl: defaultTtlS,
      subdirs: false,
    },
  });
}

const getExpiringFromResponseHeader = (res: AxiosResponse) => {
  const { expires } = res.headers;
  return expires ? Date.parse(expires) - Date.now() : defaultTtlS * 1000;
};

async function cachedAxios(
  config: AxiosRequestConfig,
  requestId: string,
  requestStart: Date,
  logger:
    | Console
    | {
        [key: string]: (args: string) => void;
      },
  useCache = true,
): Promise<any> {
  const isServer = useIsServer();
  const cacheKey = JSON.stringify(config);
  const cacheOnServer = useCache && isServer && config.method && config.method.toUpperCase() === HttpMethods.GET;

  let source;
  let result;

  if (cacheOnServer) {
    do {
      // Try fetch from memory cache
      result = memoryCache && memoryCache.get(cacheKey);

      if (result) {
        source = 'memory';
        break;
      }

      // Try fetch from disck cache
      // eslint-disable-next-line no-await-in-loop
      result = await diskCache.get(cacheKey);
      if (result) {
        // memory cache gets busted when we exceed the number of payloads allowed or when we restart the process
        // cache back in memory when necessary
        if (memoryCache && JSON.stringify(result).length < MaxPayloadSizeForMemoryCache) {
          memoryCache.set(cacheKey, result, getExpiringFromResponseHeader(result));
        }

        source = 'disk';
        break;
      }
      // eslint-disable-next-line no-constant-condition
    } while (false);
  }

  if (!result) {
    // Try fetch from HTTP
    delete config.adapter;
    source = 'http';

    try {
      const req = requestsWaitingResponse[cacheKey] || Axios(config);
      requestsWaitingResponse[cacheKey] = req;

      const response = await req;
      delete requestsWaitingResponse[cacheKey];

      result = { ...response };
      delete result.config;
      delete result.request;
    } catch (err: any) {
      delete requestsWaitingResponse[cacheKey];
      return err.response;
    }

    const expiresIn = getExpiringFromResponseHeader(result);

    if (cacheOnServer && expiresIn > 0) {
      if (memoryCache && JSON.stringify(result).length < MaxPayloadSizeForMemoryCache) {
        memoryCache.set(cacheKey, result, expiresIn);
      }

      diskCache.set(cacheKey, result, {
        ttl: expiresIn,
      });
    }
  }

  const requestTime = new Date().getTime() - requestStart.getTime();
  logger.debug(`API RESPONSE #${requestId}: ${requestTime}ms from ${source}`);

  return result;
}

let startRequestNumber = 0;
const apiRequest = <T extends unknown>(
  url: string,
  data: string | any,
  method = HttpMethods.GET,
  resultField: string,
  rawExtraData = '',
  maxRetries = 3,
) =>
  new Promise<T>(
    // eslint-disable-next-line no-async-promise-executor
    async (resolve, reject): Promise<any | any[]> => {
      const logger = console;
      const isServer = useIsServer();
      const locale = useLocale();
      // eslint-disable-next-line no-plusplus
      const requestNumber = ++startRequestNumber;

      let loggedSuccessfullyAlready = false;
      let retry = 0;
      let newUrl = url.indexOf('http') === 0 ? url : `${getBaseUrl(locale)}${url}`;

      // fix for GTM old app-api call. Redirect all call to root path without two locale letters
      newUrl = newUrl.replace(/\/[a-z]{2}\/app-api\/category\/gtm-products/gi, '/app-api/category/gtm-products');

      const options: AxiosRequestConfig = { method };
      options.headers = { 'Content-Type': 'application/json' };
      if (isServer) {
        options.headers['Accept-Encoding'] = 'gzip';
        options.headers['User-Agent'] = (process as any).__apiUserAgentToken;
      }

      const { useCache = true, ...restData } = data || {};

      if (method === HttpMethods.POST) {
        const cookie = useCookies();

        if (isServer && cookie) {
          options.headers.Cookie = cookie;
        }

        options.data = JSON.stringify({
          ...restData,
          clientInfo,
        });
      } else if (restData) {
        const queryData = Object.keys(restData)
          .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(restData[key])}`)
          .join('&');
        newUrl += `?${queryData}${rawExtraData ? `&${rawExtraData}` : ''}`;
      }

      const isUnauthorized = (r?: { status: number }) => r && r.status === UNAUTHORIZED;
      const request = async () => {
        retry += 1;

        const requestId = `${requestNumber}/${retry}: ${newUrl}`;
        const requestStart = new Date();

        logger.debug(`API REQUEST #${requestId} ${JSON.stringify(data)}`);

        try {
          const result: any = await cachedAxios(
            {
              url: newUrl,
              ...options,
            },
            requestId,
            requestStart,
            logger,
            useCache,
          );

          // login as a guest
          if (!loggedSuccessfullyAlready && isUnauthorized(result)) {
            await apiRequest(
              endpoints.registerGuest,
              { keepLogin: true },
              HttpMethods.POST,
              'data',
              // eslint-disable-next-line @typescript-eslint/no-shadow
            ).then((data: any) => {
              // store refresh token to cookies for API to work
              if (typeof document !== 'undefined') {
                document.cookie = `RTC=${data.refreshToken.token};path=/;expires=${new Date(
                  data.refreshToken.expiresAt,
                ).toUTCString()}`;
              }
              loggedSuccessfullyAlready = true;
            });

            request();
            return;
          }

          if (!result) {
            // eslint-disable-next-line no-throw-literal
            throw 'no response';
          }

          if (result.data && result.data.error && (result.data.error.code || result.data.error.message)) {
            let { error } = result.data;

            if (error) {
              error = {
                ...result.error,
                details: JSON.stringify(error.details),
                trace: JSON.stringify(error.trace),
              };
            } else {
              error = result.errorMessage;
            }

            throw error;
          } else {
            resolve(get(result.data, resultField));
          }
        } catch (e) {
          const error = `API ERROR #${requestId}`;
          logger.warn(error);
          logger.warn(e);

          if (retry < maxRetries) {
            setTimeout(request, retry * 1000);
          } else {
            // eslint-disable-next-line prefer-promise-reject-errors
            reject(`${error}: ${JSON.stringify(e)}`);
          }
        }
      };

      // noinspection JSIgnoredPromiseFromCall
      request();
    },
  );

export default apiRequest;
